import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Hls from 'hls.js';
import VisibilitySensor from 'react-visibility-sensor';

class VideoHls extends Component {
  static propTypes = {
    src: PropTypes.string.isRequired,
    poster: PropTypes.string.isRequired,
    controls: PropTypes.bool,
    autoPlay: PropTypes.bool,
    muted: PropTypes.bool,
    loop: PropTypes.bool,
    playStopOnViewportChange: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    posterFull: PropTypes.bool,
    onEnded: PropTypes.func,
    blur: PropTypes.bool,
    playsinline: PropTypes.bool,
    callback: PropTypes.func
  };

  static defaultProps = {
    controls: true,
    autoPlay: false,
    muted: false,
    loop: false,
    fullHeight: false,
    playStopOnViewportChange: false,
    onEnded: () => {},
    handleEnded: () => {},
    blur: false,
    playsinline: false,
    callback: () => {}
  };

  constructor(props) {
    super(props);

    this.initVideo = this.initVideo.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.play = this.play.bind(this);
    this.stop = this.stop.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.getCurrentTime = this.getCurrentTime.bind(this);
    this.seekTo = this.seekTo.bind(this);
    this.pause = this.pause.bind(this);
    this.isPlaying = this.isPlaying.bind(this);
    this.videoRef = React.createRef();
  }

  state = {
    initialized: false,
    canplay: false
  };

  initVideo() {
    const video = this.videoRef.current;
    const { autoPlay, src } = this.props;
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (autoPlay) {
          this.play();
        }
      });
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        this.setState({ canplay: true });
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.addEventListener('loadedmetadata', () => {
        if (autoPlay) {
          this.play();
        }
      });
      video.addEventListener('canplay', () => {
        this.setState({ canplay: true });
      });
    }
    this.setState({ initialized: true }, this.props.callback());
  }

  onEnded(event) {
    const isEnded = event.currentTarget.currentTime === event.currentTarget.duration;
    if (isEnded && this.props.onEnded) {
      this.props.onEnded();
    }
  }

  onVisibilityChange(isVisible) {
    const { initialized } = this.state;
    const { playStopOnViewportChange } = this.props;
    if (isVisible && !initialized) {
      this.initVideo();
    }
    if (isVisible && initialized && playStopOnViewportChange) {
      this.play();
    }
    if (!isVisible && initialized && playStopOnViewportChange) {
      this.stop();
    }
  }

  isPlaying() {
    const video = this.videoRef.current;
    const isPlaying =
      video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
    return isPlaying;
  }

  play() {
    const video = this.videoRef.current;
    if (!this.isPlaying()) {
      video.play();
    }
  }

  pause() {
    const video = this.videoRef.current;
    if (this.isPlaying()) {
      video.pause();
    }
  }

  stop() {
    const video = this.videoRef.current;
    if (this.isPlaying()) {
      video.currentTime = 0;
      video.pause();
    }
  }

  getCurrentTime() {
    const video = this.videoRef.current;
    return video.currentTime;
  }

  seekTo(currentTime) {
    const video = this.videoRef.current;
    video.currentTime = currentTime;
  }

  render() {
    const {
      handleEnded,
      autoPlay,
      poster,
      muted,
      controls,
      loop,
      fullHeight,
      fullWidth,
      posterFull,
      blur,
      playsinline
    } = this.props;
    return (
      <div
        className={cx('wrapper', {
          'wrapper--full-height': fullHeight
        })}
      >
        <VisibilitySensor onChange={this.onVisibilityChange} scrollCheck partialVisibility>
          <>
            {poster && (
              <img
                src={poster}
                alt=""
                className={cx('poster', {
                  'poster--hide': this.state.canplay,
                  'poster--blur': blur,
                  'poster--full': posterFull
                })}
              />
            )}
            <video
              className={cx('video', {
                'video--full-width': fullWidth,
                'video--full-height': fullHeight
              })}
              width={this.props.full ? '100%' : 'auto'}
              height="auto"
              autoPlay={autoPlay}
              muted={muted}
              loop={loop}
              controls={controls}
              ref={this.videoRef}
              onTimeUpdate={this.onEnded}
              onEnded={handleEnded}
              playsInline={playsinline}
            />
          </>
        </VisibilitySensor>
        <style jsx>{`
          .wrapper {
            position: relative;
            overflow: hidden;
          }
          .wrapper--full-height {
            height: 100%;
          }

          .video {
            max-width: 100%;
            display: inline-block;
            vertical-align: middle;
          }
          .video--full-width {
            width: 100%;
            display: block;
            max-width: none;
          }
          .video--full-height {
            width: 100%;
            height: 100%;
            display: block;
            max-width: none;
            object-fit: cover;
          }

          .poster {
            opacity: 1;
            width: 100%;
            height: auto;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 10;
            object-fit: cover;
          }
          .poster--blur {
            filter: blur(50px);
            transition: all 2s;
          }
          .poster--hide {
            pointer-events: none;
            opacity: 0;
          }
          .poster--full {
            height: 100%;
          }
        `}</style>
      </div>
    );
  }
}

export default VideoHls;
